/* eslint-disable sonarjs/no-duplicate-string */
import React from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'

import { COLORS, FionaProducts, FionaTopics, PAGE_BREAKPOINTS, ROUTES } from 'parameters/index'
import AnalyticsService from 'services/AnalyticsLite/AnalyticsService'

interface Props {
	product: FionaProducts | FionaTopics
	title?: string
	isInsideArticle?: boolean
	isVertical?: boolean
	isButtonBlock?: boolean
}

const widgetObj = {
	[FionaProducts.LifeInsurance]: {
		icon: '/images/life-insurance-widget-icon.svg',
		text: 'Compare personalized life insurance policy quotes just for you',
		subtext: 'We match you with top carriers based on your needs.',
		path: ROUTES.LIFE_INSURANCE,
		loggedInPath: '/explore/life-insurance',
		label: 'Life Insurance'
	},
	[FionaProducts.AutoInsurance]: {
		icon: '/images/auto-insurance-widget-icon.svg',
		text: 'Compare auto insurance policies just for you.',
		subtext: '',
		path: ROUTES.AUTO_INSURANCE,
		loggedInPath: '/explore/auto-insurance/redirect',
		label: 'Auto Insurance'
	},
	[FionaProducts.Insurance]: {
		icon: '/images/life-insurance-widget-icon.svg',
		text: 'Compare personalized life insurance policy quotes just for you',
		subtext: 'We match you with top carriers based on your needs.',
		path: ROUTES.LIFE_INSURANCE,
		loggedInPath: '/explore/life-insurance',
		label: 'Life Insurance'
	},
	[FionaProducts.CreditCards]: {
		icon: '/images/credit-cards-widget-icon.svg',
		text: 'Let Fiona match you with your next credit card',
		subtext: 'From rewards to low APRs, get personalized credit card offers',
		path: ROUTES.CREDIT_CARDS,
		loggedInPath: '/explore/credit-cards',
		label: 'Credit Cards'
	},
	[FionaProducts.Loans]: {
		icon: '/images/loans-widget-icon.svg',
		text: 'The easy way to find a personal loan',
		subtext: 'Search, compare, and get recommended personalized loan offers in less than 60 seconds.',
		path: ROUTES.PERSONAL_LOANS,
		loggedInPath: '/explore/loans',
		label: 'Loans'
	},
	[FionaProducts.Savings]: {
		icon: '/images/savings-widget-icon.svg',
		text: 'Choose from the top savings account offers from the top institutions',
		subtext: 'Money Market Accounts, High-Yield Savings, and other options for your hard-earned money',
		path: ROUTES.SAVINGS,
		loggedInPath: '/explore/savings',
		label: 'Savings'
	},
	[FionaProducts.Mortgages]: {
		icon: '/images/mortgage-widget-icon.svg',
		text: 'Find low mortgage rates whether you’re home buying or refinancing',
		subtext: 'Fiona makes it easy to compare mortgage offers all in one place',
		path: '/explore/mortgage',
		loggedInPath: '/explore/mortgage',
		label: 'Mortgages'
	},
	[FionaProducts.StudentLoanRefinance]: {
		icon: '/images/loans-widget-icon.svg',
		text: "An education is forever. Your student loan shouldn't be",
		subtext: 'Use your smarts and refinance your student loan at a lower rate.',
		path: '/products/student-loan-refinance',
		loggedInPath: '/explore/student-loan-refinance',
		label: 'Student Loan Refinance'
	},
	[FionaTopics.DebtConsolidation]: {
		icon: '/images/loans-widget-icon.svg',
		text: 'Ready to consolidate your debt?',
		subtext: '',
		path: ROUTES.PERSONAL_LOANS,
		loggedInPath: ROUTES.NATIVE_LOANS,
		label: ''
	}
}

export const BasicCrossSell = ({ product, title, isInsideArticle, isVertical, isButtonBlock }: Props): JSX.Element => {
	const url = widgetObj[product].loggedInPath
	const onClick = (): void => {
		AnalyticsService.track('basic-click', {
			category: 'cross-sell',
			label: widgetObj[product].label
		})
	}

	return (
		<WidgetWrapper product={product} isVertical={isVertical}>
			{widgetObj[product].icon && <WidgetBackgroundIcon src={widgetObj[product].icon} alt="" />}
			<WidgetTextWrapper>
				{title && <WidgetTitle>{title},</WidgetTitle>}
				<WidgetText>{widgetObj[product].text}</WidgetText>
				<Text>{widgetObj[product].subtext} </Text>
			</WidgetTextWrapper>
			{isInsideArticle ? (
				<Link href={url} passHref legacyBehavior>
					<WidgetAnchor>
						<WidgetButton onClick={onClick}>Get Started</WidgetButton>
					</WidgetAnchor>
				</Link>
			) : (
				<Link href={url} passHref legacyBehavior>
					<WidgetAnchor>
						<WidgetButton onClick={onClick} style={isButtonBlock ? { width: '100%', margin: 0 } : {}}>
							Get Started
						</WidgetButton>
					</WidgetAnchor>
				</Link>
			)}
		</WidgetWrapper>
	)
}

export const WidgetWrapper = styled.div<Props>`
	width: 100%;
	position: relative;
	overflow: hidden;
	padding: 24px;
	border-radius: 8px;
	box-shadow: 0 0 20px 0 rgba(69, 69, 69, 0.16);
	display: flex;
	flex-direction: ${({ isVertical }): string => (isVertical ? 'column' : 'row')};
	height: ${({ isVertical }): string => (isVertical ? '100%' : 'unset')};
	justify-content: space-between;
	align-items: center;
	background-image: ${({ product }): string => {
		switch (product) {
			case FionaProducts.LifeInsurance:
				return 'linear-gradient(280deg, #148ae0 98%, #066db9);'
			case FionaProducts.AutoInsurance:
				return 'linear-gradient(280deg, #148ae0 98%, #066db9);'
			case FionaProducts.CreditCards:
				return 'linear-gradient(280deg, #e2ae22 98%, #c69103);'
			case FionaProducts.Loans:
				return 'linear-gradient(280deg, #14e075 98%, #24c669);'
			case FionaTopics.DebtConsolidation:
				return 'linear-gradient(280deg, #14e075 98%, #24c669);'
			case FionaProducts.Savings:
				return 'linear-gradient(283deg, #398293 98%, #0f5a6c);'
			case FionaProducts.Mortgages:
				return 'linear-gradient(280deg, #14e075 98%, #24c669);'
			default:
				return 'linear-gradient(295deg, #398293 99%, #0f5a6c);'
		}
	}};

	@media only screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		flex-direction: column;
	}
`

const WidgetBackgroundIcon = styled.img`
	position: absolute;
	width: 120px;
	top: -25px;
	left: -25px;
`

const WidgetTextWrapper = styled.div``

const WidgetTitle = styled.p`
	color: ${COLORS.WHITE};
	font-size: 18px;
	font-weight: normal;
	line-height: 1.33;
`

const WidgetText = styled.p`
	color: ${COLORS.WHITE};
	font-size: 24px;
	font-weight: bold;
	line-height: 1.33;
`

const Text = styled.p`
	color: ${COLORS.WHITE};
	font-size: 18px;
	line-height: 1.33;
	margin-top: 32px;
	margin-bottom: 24px;
`

const WidgetButton = styled.button`
	color: ${COLORS.WHITE};
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 180px;
	border-radius: 4px;
	border: 3px solid ${COLORS.WHITE};
	padding: 0 18px;
	height: 48px;
	text-transform: uppercase;
	outline: none;
	background: transparent;
	cursor: pointer;
	text-decoration: none;
	margin-left: 72px;

	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		margin-top: 20px;
	}

	@media only screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		margin-left: 0;
		width: 100%;
	}
`

const WidgetAnchor = styled.a`
	text-decoration: none;
`
